import React, { useState } from 'react';
import { Theme, Grid, Button, IconButton } from '@mui/material';

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import * as NotificationActions from 'actions/NotificationActions';

import PaymentRow from '../PaymentRow';
import { CurrencyObject, OrderPayment } from 'common/types';
import { useSelector, useDispatch } from 'react-redux';
import { RiPencilLine } from 'react-icons/ri';
import DepositSelectDialog from './DepositSelectDialog';
import { updateActivePartialOrder } from 'actions';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';
import { getUpdatedProductsWithNewDepositValue, getDepositStatus } from './utils';
import PaymentStatus from '../components/PaymentStatus';
import { getPricingString } from 'common/utils/common';
import { useTranslation } from 'services/localization/useTranslation';
import { PartialOrder } from 'common/types';
import { DepositPricing } from 'common/modules/atoms/pricing';
import { getPaymentAmountsFromOrderPayment } from 'common/modules/atoms/paymentAmounts';
import { Callable } from 'common/frontend/callable';
import { getResponsiblePersonDetails } from 'services/utils';
import DepositReserveDialog from './DepositReserveDialog';
import Spinner from 'common/components/Spinner';
import { getDepositStatusVariant } from '../../utils';
import { getDepositStatusTranslation } from 'services/translations';

export type DepositReserveType = 'MARK_AS_RESERVED' | 'RESERVE_NOW';

interface Props {
	deposit: DepositPricing;
	payment: OrderPayment;
	currency: CurrencyObject;
	showPayDialog: () => void;
	markAsReserved: () => void;
}

const DepositSection = ({ deposit, markAsReserved, currency, payment }: Props) => {
	const classes = useStyles();
	const [depositSelectDialogOpen, setDepositSelectDialogOpen] = useState(false);
	const dispatch = useDispatch();
	const orderProducts = useSelector(RentalViewSelectors.orderProducts);
	const orderInfo = useSelector(RentalViewSelectors.orderInfo)!;
	const orderShoppers = useSelector(RentalViewSelectors.orderShoppers);
	const depositType = deposit.type || 'payment';
	const orderId = orderInfo.id;
	const shopId = orderInfo.shopId;
	const responsiblePerson = getResponsiblePersonDetails(orderInfo.responsiblePerson, orderShoppers);

	const hasSavedCardPaymentInfo = !!responsiblePerson?.savedPaymentInfo;

	const { t } = useTranslation();
	const paymentAmount = getPaymentAmountsFromOrderPayment(deposit.value, currency.code, payment);
	const [paymentButtonLoading, setPaymentButtonLoading] = useState<boolean>(false);
	const [reserveDialogOpen, setReserveDialogOpen] = useState<boolean>(false);

	const totalToReserve = paymentAmount.total;
	const reservedAmount = paymentAmount.reserved;
	const chargedAmount = paymentAmount.paid;
	const unreservedAmount = paymentAmount.unreserved;
	const orderCompleted = orderInfo.rentalState === 'COMPLETED';

	const isDepositPaymentWithCard = depositType === 'payment' && hasSavedCardPaymentInfo;

	const handleDepositSelectChange = (data: DepositPricing) => {
		const { value, type, additionalInfo } = data;
		const newDepositValue = value;
		const partialProductsWithNewDeposit = getUpdatedProductsWithNewDepositValue(
			orderProducts,
			newDepositValue,
		);
		const partialOrder: PartialOrder = {
			rentalInfo: {
				id: orderInfo.id,
				data: {
					pricing: {
						...orderInfo.pricing,
						deposit: {
							value,
							type,
							...(additionalInfo != null && { additionalInfo }),
						},
					},
				},
			},
			products: partialProductsWithNewDeposit,
		};
		dispatch(updateActivePartialOrder(partialOrder));
		setDepositSelectDialogOpen(false);
	};

	const getDepositStatusText = () => {
		if (depositType === 'no-deposit') {
			return t('common:payment.noDeposit');
		}
		if (depositType === 'other') {
			return t('common:payment.item');
		}
		const depositStatus = getDepositStatus(paymentAmount).status;
		return getDepositStatusTranslation(depositStatus, t);
	};

	const handleReserveConfirm = async (type: DepositReserveType) => {
		switch (type) {
			case 'RESERVE_NOW': {
				setPaymentButtonLoading(true);
				try {
					await Callable.orders.modifications.reserveDeposit({ orderId, shopId });
				} catch (e) {
					dispatch(
						NotificationActions.showNotification({
							message: t('errors.depositReserveError', 'Error reserving the deposit'),
							variant: 'error',
						}),
					);
				} finally {
					setPaymentButtonLoading(false);
				}
				return;
			}
			case 'MARK_AS_RESERVED': {
				return markAsReserved();
			}
			default: {
				return null;
			}
		}
	};

	return (
		<>
			<PaymentStatus
				variant={getDepositStatusVariant(depositType, paymentAmount)}
				statusText={getDepositStatusText()}
				rightActionBtn={
					reservedAmount === 0 ? (
						<IconButton onClick={() => setDepositSelectDialogOpen(true)} size="large">
							<RiPencilLine />
						</IconButton>
					) : undefined
				}
			/>
			<div className={classes.paymentDetailContainer}>
				<div className={classes.paymentRowContainer}>
					{depositType === 'payment' ? (
						<PaymentRow
							description={t('shoppingCart.deposit')}
							value={getPricingString(totalToReserve, currency)}
						/>
					) : depositType === 'other' ? (
						<PaymentRow
							description={t('shoppingCart.deposit')}
							value={deposit.additionalInfo || t('common:payment.item')}
						/>
					) : null}
					{Boolean(chargedAmount) ? (
						<PaymentRow
							description={t('common:payment.charged', 'Charged')}
							value={getPricingString(chargedAmount, currency)}
						/>
					) : (
						<>
							{(reservedAmount !== totalToReserve || depositType !== 'payment') &&
								Boolean(reservedAmount) && (
									<PaymentRow
										description={t('common:payment.reserved', 'Reserved')}
										value={getPricingString(reservedAmount, currency)}
									/>
								)}
							{unreservedAmount !== totalToReserve && unreservedAmount > 0 && (
								<PaymentRow
									description={t('common:payment.unreserved', 'Unreserved')}
									value={getPricingString(Math.abs(unreservedAmount), currency)}
									variant="subtitle2"
								/>
							)}
						</>
					)}
				</div>
			</div>
			{depositType === 'payment' && (
				<Grid container justifyContent="center" alignItems="center">
					{unreservedAmount > 0 && !orderCompleted ? (
						<Grid item xs={12}>
							<Button
								fullWidth
								variant="outlined"
								size="small"
								onClick={() => setReserveDialogOpen(true)}
								disabled={paymentButtonLoading}
							>
								{paymentButtonLoading ? <Spinner padding={8} /> : t('payment.reserve', 'Reserve')}
							</Button>
						</Grid>
					) : /* 
                This needs to be added later to support POS deposit payments
                ) : unreservedAmount > 0 && useAsPos ? (
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={showPayDialog}
                        >
                            Reserve
                        </Button>
                    </Grid>*/
					null}
				</Grid>
			)}
			{depositSelectDialogOpen && (
				<DepositSelectDialog
					deposit={deposit}
					currency={currency}
					onCancel={() => setDepositSelectDialogOpen(false)}
					onSave={handleDepositSelectChange}
				/>
			)}
			{reserveDialogOpen && (
				<DepositReserveDialog
					onCancel={() => setReserveDialogOpen(false)}
					onConfirm={(type) => {
						setReserveDialogOpen(false);
						handleReserveConfirm(type);
					}}
					reservationType={isDepositPaymentWithCard ? 'payment_card' : 'manual'}
				/>
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			padding: theme.spacing(1, 0),
		},
		paymentIndicator: {
			width: 16,
			height: 16,
			borderRadius: 30,
			marginRight: theme.spacing(2),
		},
		paymentDetailContainer: {
			maxHeight: 150,
			display: 'flex',
			flexDirection: 'column',
			paddingBottom: theme.spacing(2),
		},
		paymentRowContainer: {
			flexGrow: 1,
			padding: theme.spacing(0.5, 0),
		},
		leftIcon: {
			marginRight: theme.spacing(1),
		},
	}),
);

export default DepositSection;
